import BaseStore from 'customer-flows/stores/base-store'
import { isAvailableStatus, isBookableStatus } from 'customer-flows/availability/status'

export default class ActivityAvailabilityStoreClass extends BaseStore {

  /**
   * For activities, spots is a shared pool capacity — use it directly when present.
   * When spots is not set, fall back to the max qty across ticket-type entries.
   */
  getMaxSpotsLeftForSlot(slot: AvailabilityTime): number {
    if (slot.spots != null) return slot.spots
    return slot.availability.reduce((max, a) => Math.max(max, a.qty), 0)
  }

  hasAvailabilityForSelectedTickets(
    slot: AvailabilityTime,
    requiredQtyByTicketId: Record<string, number>
  ): boolean {
    const requiredEntries = Object.entries(requiredQtyByTicketId)
    if (requiredEntries.length === 0) return true
    // Non-bookable slots can't be selected — assume available for display purposes
    if (!isBookableStatus(slot.status)) return true
    if (!isAvailableStatus(slot.status)) return false
    if (!this.hasSufficientSpots(slot, requiredEntries)) return false
    return this.hasSufficientPerTicketQty(slot, requiredEntries)
  }

  private hasSufficientSpots(
    slot: AvailabilityTime,
    requiredEntries: [string, number][]
  ): boolean {
    if (slot.spots == null) return true
    const totalRequired = requiredEntries.reduce((sum, [, qty]) => sum + qty, 0)
    return totalRequired <= slot.spots
  }

  private hasSufficientPerTicketQty(
    slot: AvailabilityTime,
    requiredEntries: [string, number][]
  ): boolean {
    const maxQtyByTicketId = slot.availability.reduce((acc, a) => {
      if (!a.resourceOptionId) return acc
      acc[a.resourceOptionId] = Math.max(acc[a.resourceOptionId] ?? 0, a.qty)
      return acc
    }, {} as Record<string, number>)

    return requiredEntries.every(([ticketId, requiredQty]) => {
      return (maxQtyByTicketId[ticketId] ?? 0) >= requiredQty
    })
  }
}

declare global {
  export type ActivityAvailabilityStore = ActivityAvailabilityStoreClass
  export interface StoreRegistry {
    'availability/activity': ActivityAvailabilityStore
  }
}
